import {
  computed,
  onMounted,
  onUnmounted,
  shallowRef,
  toValue,
  watch,
  type MaybeRefOrGetter,
} from 'vue'
import { storeToRefs } from 'pinia'
import {
  ReportEvents,
  SHAFT_TYPE_HEAD,
  SHAFT_TYPE_ID_MASK,
  type GcomeKeyDebugUpload,
  type GcomeKeyTravel,
} from '@rapoo/device-sdk'
import { useDeviceStore } from '@/stores'
import { useItinerarySDK } from './useItinerarySDK'

/** 协议「按键行程」字段范围：0-40 档，1 档 = 0.1mm。 */
const TRAVEL_STEP_MAX = 40
/** 协议按键索引上限（触发行程参数区固定 128 键）。 */
const KEY_INDEX_MAX = 128
/** Arc 仪表盘的行程量程：3.4mm，即 34 档（协议行程可到 4.0mm）。 */
const GAUGE_STEP_MAX = 33
/** Arc 仪表盘的级数值域（ArcGaugeSlider 按 0-255 换算指针角度与圆弧扫过角度）。 */
const GAUGE_VALUE_MAX = 255
/** 一档行程对应的毫米数。 */
const MM_PER_STEP = 0.1
/** travel 单位为 0.01mm，与行程档位（0.1mm）的换算比例。 */
const TRIGGER_UNITS_PER_STEP = 10
/** 快速按下仅有少量上报帧时，释放归零前保留仪表展示的时长。 */
const RELEASE_RESET_DELAY = 80
/** 每帧向目标行程靠近的比例，用于补足稀疏设备上报的展示帧。 */
const DISPLAY_TRAVEL_EASING = 0.25

type UseDemoModeOptions = {
  /** 当前行程页是否处于演示模式（false 时下发关闭并停止监听）。 */
  enabled: MaybeRefOrGetter<boolean>
  /**
   * 按键索引查询该键当前配置的行程参数。
   * hasCrossedAbsoluteTrigger 取活跃键自身的 travel，保证多键场景下各键阈值独立判断；
   * 索引未知时传 -1，由调用方回退到正在编辑的参数。
   */
  getKeyTravel: (index: number) => GcomeKeyTravel
}

/**
 * key_debug 上报标志（0xA0）与「触发行程参数区」响应撞码：
 * 参数区每键 8 字节，字节0 = SHAFT_TYPE_HEAD | 轴体类型，轴体类型为 0 时该字节恰好等于 0xA0，
 * 于是读取行程参数（页面初始化即会读一次）的响应会被当成一份按键调试上报，
 * 其「行程」字段实际取到的是相邻键的 RT 参数，未按键就把指针拉到满档。
 *
 * 参数区是 8 字节步进，raw[0]/raw[8]/raw[16] 都落在轴体类型字节上，
 * 而真实上报的 raw[8]（hex 变化量）与 raw[16]（当前 ADC 高字节）不会同时带这个校验头。
 */
const isTravelBlockEcho = (raw: number[]) =>
  (raw[8] & ~SHAFT_TYPE_ID_MASK) === SHAFT_TYPE_HEAD &&
  (raw[16] & ~SHAFT_TYPE_ID_MASK) === SHAFT_TYPE_HEAD

/** 取上报中的行程档位；超出协议范围或识别为参数区回声时返回 null（丢弃该帧，不做钳制）。 */
const readTravelStep = (data: GcomeKeyDebugUpload): number | null => {
  const step = data.mmTravel
  if (!Number.isInteger(step) || step < 0 || step > TRAVEL_STEP_MAX) return null
  if (isTravelBlockEcho(data.raw)) return null
  return step
}

/** 取上报中的按键索引（keyInfo[0]），越界时返回 null。 */
const readKeyIndex = (data: GcomeKeyDebugUpload): number | null => {
  const index = data.keyInfo[0]
  return Number.isInteger(index) && index >= 0 && index < KEY_INDEX_MAX ? index : null
}

/**
 * 按键级数演示：开启设备调试上报（功能变量区字节7 Bit3）并把实时行程喂给 ArcGaugeSlider。
 *
 * 流程参考 sdk-test 的 useTravelDemo / useFuncDebugUpload：
 * switchDemoMode(true) 成功后才在同一 device 引用上绑定 KeyCalibUpload，
 * 解绑闭包持有该引用，避免设备切换后误操作旧设备。
 * 所有开关都串到一条 Promise 队列上，防止 enabled 快速抖动导致重复绑定或开关乱序。
 */
export function useDemoMode(options: UseDemoModeOptions) {
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)
  const { switchDemoMode } = useItinerarySDK()

  const enabled = computed(() => toValue(options.enabled))
  const { getKeyTravel } = options

  // ==================== 展示数据 ====================

  /** 演示模式是否已开启并处于监听中。 */
  const demoActive = shallowRef(false)
  /** 设备上报的实时行程档位（0-40，1 档 = 0.1mm）。 */
  const travelStep = shallowRef(0)
  /** 插值后的仪表展示行程档位。 */
  const displayTravelStep = shallowRef(0)
  /** 当前上报行程的按键索引，未知时为 null。 */
  const activeKeyIndex = shallowRef<number | null>(null)
  let releaseResetTimer: ReturnType<typeof setTimeout> | undefined
  let displayTravelFrame: number | undefined

  /**
   * 落在仪表盘量程内的行程档位：协议行程可到 4.0mm，超出 3.4mm 的部分按满量程显示，
   * 使级数与毫米读数都停在量程上限，不会越出仪表盘。
   */
  const gaugeStep = computed(() => Math.min(GAUGE_STEP_MAX, displayTravelStep.value))

  /** ArcGaugeSlider 的级数（0-255，0 档对应 0、34 档对应 255）。 */
  const realTimeTravel = computed(() =>
    Math.round((gaugeStep.value / GAUGE_STEP_MAX) * GAUGE_VALUE_MAX),
  )

  /** 实时行程的毫米值（0-3.4mm，与级数同源，未触发时为 0）。 */
  const realTimeTravelMm = computed(() => gaugeStep.value * MM_PER_STEP)

  /** 实时行程是否已越过活跃键自身的绝对触发行程（决定圆弧高亮色）。 */
  const hasCrossedAbsoluteTrigger = computed(() => {
    if (!demoActive.value || displayTravelStep.value <= 0) return false
    const { travel } = getKeyTravel(activeKeyIndex.value ?? -1)
    return displayTravelStep.value * TRIGGER_UNITS_PER_STEP >= travel
  })

  const animateDisplayTravel = () => {
    const difference = travelStep.value - displayTravelStep.value
    if (Math.abs(difference) < 0.05) {
      displayTravelStep.value = travelStep.value
      displayTravelFrame = undefined
      return
    }
    displayTravelStep.value += difference * DISPLAY_TRAVEL_EASING
    displayTravelFrame = requestAnimationFrame(animateDisplayTravel)
  }

  const syncDisplayTravel = () => {
    if (displayTravelFrame !== undefined) return
    displayTravelFrame = requestAnimationFrame(animateDisplayTravel)
  }

  const setTravelStep = (step: number) => {
    travelStep.value = step
    syncDisplayTravel()
  }

  const stopDisplayTravel = () => {
    if (displayTravelFrame !== undefined) {
      cancelAnimationFrame(displayTravelFrame)
      displayTravelFrame = undefined
    }
  }

  const clearReleaseResetTimer = () => {
    if (!releaseResetTimer) return
    clearTimeout(releaseResetTimer)
    releaseResetTimer = undefined
  }

  const resetTravel = () => {
    clearReleaseResetTimer()
    stopDisplayTravel()
    travelStep.value = 0
    displayTravelStep.value = 0
    activeKeyIndex.value = null
  }

  /** 释放帧会立即归零，短暂保留最近有效值，保证快速按压也能完成一次仪表绘制。 */
  const scheduleReleaseReset = () => {
    clearReleaseResetTimer()
    releaseResetTimer = setTimeout(() => {
      releaseResetTimer = undefined
      setTravelStep(0)
      activeKeyIndex.value = null
    }, RELEASE_RESET_DELAY)
  }

  // ==================== 上报监听 ====================

  const onTravelReport = (data: GcomeKeyDebugUpload) => {
    if (!demoActive.value || !enabled.value) return
    const step = readTravelStep(data)
    if (step === null) return
    activeKeyIndex.value = readKeyIndex(data)
    if (step === 0 && travelStep.value > 0) {
      scheduleReleaseReset()
      return
    }
    clearReleaseResetTimer()
    setTravelStep(step)
  }

  /** 解绑监听的闭包，持有绑定时的 device 引用。 */
  let unbind: (() => void) | null = null
  /** 已开启演示模式的设备，用于异步切换或设备切换后的正确收尾。 */
  let demoDevice: typeof currentDevice.value
  let isMounted = false

  /** 只清理本地监听与展示数据，不下发指令（设备切换场景：旧设备已不可用）。 */
  const detach = () => {
    unbind?.()
    unbind = null
    demoActive.value = false
    resetTravel()
  }

  // ==================== 演示模式开关 ====================

  const startDemo = async () => {
    if (!isMounted || demoActive.value || demoDevice) return
    const device = currentDevice.value
    const feature = device?.features.keyTravel
    if (!feature) return
    try {
      // useItinerarySDK 已记录并上报失败原因，这里只需放弃本次开启
      await switchDemoMode(true)
    } catch {
      return
    }
    demoDevice = device
    // 等待期间可能已切换设备或退出演示，此时不绑定（关闭指令由队列中的 stopDemo 负责）
    if (currentDevice.value !== device || !enabled.value || !isMounted) return
    device.on(ReportEvents.KeyCalibUpload, onTravelReport)
    unbind = () => device.off(ReportEvents.KeyCalibUpload, onTravelReport)
    demoActive.value = true
  }

  const stopDemo = async () => {
    const device = demoDevice
    // 先解绑再下发关闭，避免关闭指令的读-改-写响应被当成上报
    detach()
    demoDevice = null
    if (!device) return
    if (currentDevice.value === device) {
      await switchDemoMode(false).catch(() => undefined)
      return
    }
    const feature = device.features.keyTravel
    if (!feature) return
    await feature.switchDemoMode(false).catch(() => undefined)
  }

  /** 串行化开关，避免 enabled 抖动时 switchDemoMode 交叉执行。 */
  let queue = Promise.resolve()
  const sync = (on: boolean) => {
    queue = queue.then(() => (on ? startDemo() : stopDemo())).catch(() => undefined)
  }

  // 父组件 onMounted 晚于子组件：此时开启演示，才能覆盖子组件可能已打开的校准状态
  onMounted(() => {
    isMounted = true
    if (enabled.value) sync(true)
  })

  watch(enabled, (on) => sync(on))

  // 设备切换或 keyTravel feature 挂载完成（热插拔）后重新接入
  watch(
    () => currentDevice.value?.features.keyTravel,
    () => {
      detach()
      sync(false)
      if (enabled.value && isMounted) sync(true)
    },
  )

  // 离开页面时归还设备状态：调试上报常开会让键盘持续发包
  onUnmounted(() => {
    isMounted = false
    detach()
    sync(false)
  })

  return {
    demoActive,
    realTimeTravel,
    realTimeTravelMm,
    hasCrossedAbsoluteTrigger,
    activeKeyIndex,
  }
}
